import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Clock, Crown, Star, Tag } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ServiceDialog } from "@/components/ServiceDialog";
import { PriceLock, hasPrice } from "@/components/PriceLock";

export const ServiceCard = ({ service, index }) => {
  const { lang, t } = useLanguage();
  const [open, setOpen] = useState(false);

  const featured = service.id === "magia_suprema";
  const elite = service.id === "poder_maximo" || service.id === "pacto_satanico" || service.id === "pacto_luz";

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, delay: (index % 3) * 0.1 }}
        className={`relative rounded-2xl p-8 hover-ring glass-dark flex flex-col h-full ${
          featured ? "border-[#d4af37]/60 shadow-[0_0_60px_rgba(212,175,55,0.15)]" : ""
        }`}
        data-testid={`service-card-${service.id}`}
      >
        {service.badge && (
          <div
            className={`absolute -top-3 left-6 px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded-full font-semibold ${
              featured
                ? "bg-[#d4af37] text-black"
                : elite
                ? "bg-[#5A0001] text-[#f3cd59] border border-[#d4af37]/40"
                : "bg-black text-[#d4af37] border border-[#d4af37]/40"
            }`}
          >
            {featured && <Star className="inline w-3 h-3 mr-1 mb-0.5" />}
            {elite && <Crown className="inline w-3 h-3 mr-1 mb-0.5" />}
            {service.badge[lang]}
          </div>
        )}

        <div className="mb-4">
          <h3 className="font-serif text-2xl lg:text-3xl text-white leading-tight mb-1">
            {service.name[lang]}
          </h3>
          {service.subtitle?.[lang] && (
            <p className="text-xs uppercase tracking-widest text-[#d4af37]/80">
              {service.subtitle[lang]}
            </p>
          )}
        </div>

        {service.discountPercent && (
          <div
            data-testid={`service-discount-${service.id}`}
            className="inline-flex items-center gap-1.5 self-start mb-4 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-[11px] uppercase tracking-widest font-semibold"
          >
            <Tag className="w-3 h-3" />
            {service.discountLabel || `-${service.discountPercent}%`}
            {service.discountUntil && (
              <span className="normal-case tracking-normal text-emerald-300/80">
                · {lang === "es" ? "hasta" : "until"} {new Date(service.discountUntil).toLocaleDateString(lang === "es" ? "es-MX" : "en-US")}
              </span>
            )}
          </div>
        )}

        {service.description?.[lang] && (
          <p className="text-sm text-neutral-400 leading-relaxed mb-5 italic">
            "{service.description[lang]}"
          </p>
        )}

        {service.duration?.[lang] && (
          <div className="flex items-center gap-2 text-xs text-neutral-300 mb-5">
            <Clock className="w-3.5 h-3.5 text-[#d4af37]" />
            {service.duration[lang]}
          </div>
        )}

        <ul className="space-y-2 mb-7 flex-1">
          {(service.features?.[lang] || []).slice(0, 4).map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
              <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
          {(service.features?.[lang] || []).length > 4 && (
            <li className="text-xs text-[#d4af37]/70 italic pl-6">
              +{service.features[lang].length - 4} {lang === "es" ? "más" : "more"}
            </li>
          )}
        </ul>

        <div className="border-t border-white/10 pt-5 mb-5">
          {!hasPrice(service.priceMXN) ? (
            <div className="flex flex-col items-start gap-2" data-testid={`service-price-locked-${service.id}`}>
              {service.modalities?.length > 0 && (
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  {service.modalities.map((m) => m.label[lang]).join(" · ")}
                </div>
              )}
              <PriceLock lang={lang} testId={`service-price-lock-${service.id}`} />
            </div>
          ) : service.modalities && service.modalities.length > 0 ? (
            <div className="space-y-2" data-testid={`service-modalities-${service.id}`}>
              {service.modalities.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-white/[0.03] border border-white/10"
                  data-testid={`modality-row-${service.id}-${m.id}`}
                >
                  <span className="text-[11px] uppercase tracking-widest text-[#d4af37]/90 font-semibold">
                    {m.label[lang]}
                  </span>
                  <span className="text-sm text-white font-serif whitespace-nowrap">
                    ${m.priceMXN.toLocaleString("es-MX")}{" "}
                    <span className="text-[10px] text-neutral-500">MXN</span>
                    <span className="mx-1.5 text-neutral-600">/</span>
                    <span className="gold-text">${m.priceUSD.toLocaleString("en-US")}</span>{" "}
                    <span className="text-[10px] text-neutral-500">USD</span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-end justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">MXN</div>
                {service.originalPriceMXN && (
                  <div className="text-xs text-neutral-500 line-through" data-testid={`service-original-mxn-${service.id}`}>
                    ${Number(service.originalPriceMXN).toLocaleString("es-MX")}
                  </div>
                )}
                <div className="font-serif text-2xl text-white" data-testid={`service-price-mxn-${service.id}`}>
                  ${service.priceMXN.toLocaleString("es-MX")}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">USD</div>
                {service.originalPriceUSD && (
                  <div className="text-xs text-neutral-500 line-through">
                    ${Number(service.originalPriceUSD).toLocaleString("en-US")}
                  </div>
                )}
                <div className="font-serif text-2xl gold-text" data-testid={`service-price-usd-${service.id}`}>
                  ${service.priceUSD.toLocaleString("en-US")}
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          data-testid={`service-cta-${service.id}`}
          onClick={() => setOpen(true)}
          className={`w-full rounded-full py-3 uppercase text-xs tracking-[0.2em] font-semibold transition-all ${
            featured ? "btn-gold" : "btn-outline-gold"
          }`}
        >
          {t("card.viewDetails")}
        </button>
      </motion.div>

      <ServiceDialog service={service} open={open} onOpenChange={setOpen} />
    </>
  );
};
